import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { apiClient } from '@/lib/api'
import { Coins, Plus } from 'lucide-react'

interface SmsCreditsModalProps {
  isOpen: boolean
  onClose: () => void
  tenant: any
  onSuccess: () => void
}

export function SmsCreditsModal({ isOpen, onClose, tenant, onSuccess }: SmsCreditsModalProps) {
  const [loading, setLoading] = useState(false)
  const [granting, setGranting] = useState(false)
  const [error, setError] = useState('')
  const [currentBalance, setCurrentBalance] = useState(0)
  const [creditsToGrant, setCreditsToGrant] = useState('')

  const tenantId = tenant?.tenant_id || (tenant?.pk ? tenant.pk.split('#')[0].replace('t_', '') : '')

  useEffect(() => {
    if (isOpen && tenantId) {
      fetchCredits()
    }
  }, [isOpen, tenantId])

  const fetchCredits = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await apiClient.getPlanLimits(tenantId)
      setCurrentBalance(response.sms_balance ?? response.remaining ?? 0)
    } catch (err) {
      setError('Failed to load SMS credits')
    } finally {
      setLoading(false)
    }
  }

  const handleGrantCredits = async () => {
    const amount = parseInt(creditsToGrant)
    if (isNaN(amount) || amount === 0) {
      setError('Please enter a valid number (positive to add, negative to remove)')
      return
    }

    try {
      setGranting(true)
      setError('')
      
      const response = await apiClient.grantSmsCredits(tenantId, amount)
      
      if (response.success) {
        setCreditsToGrant('')
        await fetchCredits()
        // Show success message
        const action = amount > 0 ? 'added' : 'removed'
        const displayAmount = Math.abs(amount)
        setError(`✓ Successfully ${action} ${displayAmount} credits`)
        setTimeout(() => setError(''), 3000)
        onSuccess()
      } else {
        setError('Failed to grant SMS credits')
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to grant SMS credits'
      setError(errorMessage)
    } finally {
      setGranting(false)
    }
  }

  if (!tenant) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-green-600" />
            SMS Credits Management
          </DialogTitle>
          <DialogDescription>
            Grant free SMS credits to {tenant.display_name || tenantId}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">Loading credits...</div>
        ) : (
          <div className="space-y-6">
            {/* Current Balance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Current Balance</CardTitle>
                <CardDescription>
                  Available SMS credits for this tenant
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">SMS Credits</span>
                  <Badge variant="outline" className="text-lg px-4 py-1">
                    {currentBalance.toLocaleString()}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Grant Credits */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Grant Credits</CardTitle>
                <CardDescription>
                  Add free SMS credits to tenant balance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="credits">Number of Credits</Label>
                  <Input
                    id="credits"
                    type="number"
                    placeholder="100"
                    value={creditsToGrant}
                    onChange={(e) => setCreditsToGrant(e.target.value)}
                  />
                  <p className="text-xs text-gray-500">
                    Enter positive number to add credits, negative to remove (e.g., -50 to remove 50 credits).
                  </p>
                </div>

                <Button
                  onClick={handleGrantCredits}
                  disabled={granting || !creditsToGrant}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {granting ? 'Granting...' : 'Grant Credits'}
                </Button>
              </CardContent>
            </Card>

            {error && (
              <div className={`text-sm p-3 rounded ${
                error.startsWith('✓') 
                  ? 'text-green-600 bg-green-50' 
                  : 'text-red-600 bg-red-50'
              }`}>
                {error}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
